import React, { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Trash2, Clock, DollarSign, CheckCircle2, ClipboardList, Circle, Landmark, Camera, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../console/api";
import { BankAuditBoard } from "./BumfaceBankAudit";
import { StaffAvatar, resizePhoto } from "./AdminStaff";

const AUBERGINE = "#5A4565";
const CREAM = "#FBF6EA";
const EDGE = "#EFE4CC";
const GREEN = "#2F4A2A";
const BERRY = "#6E1E3A";
const GOLD = "#C9A268";

const today = () => new Date().toISOString().slice(0, 10);
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function AdminBumface() {
  const [tab, setTab] = useState("hours");
  return (
    <div data-testid="admin-bumface" className="space-y-6" style={{ color: AUBERGINE }}>
      <header>
        <div className="text-[10px] tracking-[0.45em] uppercase font-semibold" style={{ color: BERRY }}>
          ✦ Staff Hours &amp; Tasks
        </div>
        <h1 className="text-4xl mt-1 leading-tight">Bumface Database</h1>
        <p className="italic mt-1 text-sm opacity-70">
          Data entry &amp; auditing · $40/hr cash. Log hours on one board, tick off jobs on the other.
        </p>
      </header>

      <div className="flex gap-2">
        <TabBtn active={tab === "hours"} onClick={() => setTab("hours")} testid="bumface-tab-hours">
          <Clock className="w-4 h-4" /> Hours Log
        </TabBtn>
        <TabBtn active={tab === "tasks"} onClick={() => setTab("tasks")} testid="bumface-tab-tasks">
          <ClipboardList className="w-4 h-4" /> Jobs &amp; Tasks
        </TabBtn>
        <TabBtn active={tab === "bank"} onClick={() => setTab("bank")} testid="bumface-tab-bank">
          <Landmark className="w-4 h-4" /> Bank Auditor
        </TabBtn>
      </div>

      {tab === "hours" ? <HoursBoard /> : tab === "tasks" ? <TasksBoard /> : <BankAuditBoard />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="inline-flex items-center gap-2 px-5 h-10 rounded-full text-sm font-semibold border-2 transition-colors"
      style={active
        ? { backgroundColor: BERRY, borderColor: BERRY, color: "#fff" }
        : { borderColor: EDGE, color: AUBERGINE, backgroundColor: "#fff" }}
    >
      {children}
    </button>
  );
}

/* ------------------------------------------------------- HOURS BOARD ---- */

function HoursBoard() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ date: today(), hours: "", notes: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/hours");
      setRows(r.data.entries || []);
      setSummary(r.data.summary || {});
    } catch {
      toast.error("Couldn't load the hours log.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    const hours = parseFloat(form.hours);
    if (!form.date || !hours || hours <= 0) { toast.error("Enter a date and hours worked."); return; }
    setBusy("add");
    try {
      await api.post("/console/hours", { date: form.date, hours, notes: form.notes });
      setForm({ date: today(), hours: "", notes: "" });
      toast.success("Hours logged.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(""); }
  };

  const togglePaid = async (row) => {
    setBusy(row.id);
    try {
      await api.patch(`/console/hours/${row.id}`, { paid: !row.paid });
      await load();
    } catch { toast.error("Couldn't update."); } finally { setBusy(""); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete the entry for ${row.date} (${row.hours} hrs)?`)) return;
    setBusy(row.id);
    try {
      await api.delete(`/console/hours/${row.id}`);
      toast.success("Entry deleted.");
      await load();
    } catch { toast.error("Couldn't delete."); } finally { setBusy(""); }
  };

  return (
    <div className="space-y-5" data-testid="bumface-hours-board">
      <div className="grid grid-cols-3 gap-3">
        <Tile label="Total hours" value={`${summary.total_hours ?? 0} hrs`} Icon={Clock} accent={AUBERGINE} testid="bumface-tile-hours" />
        <Tile label="Owed (unpaid)" value={money(summary.unpaid_amount)} Icon={DollarSign} accent={BERRY} testid="bumface-tile-owed" />
        <Tile label="Paid to date" value={money(summary.paid_amount)} Icon={CheckCircle2} accent={GREEN} testid="bumface-tile-paid" />
      </div>

      <form onSubmit={add} className="bg-white border rounded-md p-4 grid grid-cols-1 sm:grid-cols-[160px_120px_1fr_auto] gap-3 items-end" style={{ borderColor: EDGE }}>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Date</label>
          <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1 w-full h-10 px-3 border rounded" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid="bumface-date" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Hours</label>
          <input type="number" step="0.25" min="0.25" max="24" placeholder="3.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} className="mt-1 w-full h-10 px-3 border rounded" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid="bumface-hours" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Notes (what got done)</label>
          <input placeholder="Entered 32 historical orders, audited March invoices…" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full h-10 px-3 border rounded" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid="bumface-notes" />
        </div>
        <button type="submit" disabled={busy === "add"} className="h-10 px-5 rounded text-white text-sm inline-flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: BERRY }} data-testid="bumface-add">
          {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Log hours
        </button>
      </form>

      <div className="bg-white border rounded-md overflow-hidden" style={{ borderColor: EDGE }}>
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center italic opacity-60" data-testid="bumface-empty">No hours logged yet. First entry above ↑</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.25em] uppercase" style={{ backgroundColor: CREAM }}>
                <th className="px-4 py-2.5">Date</th>
                <th className="px-4 py-2.5">Hours</th>
                <th className="px-4 py-2.5">Owed</th>
                <th className="px-4 py-2.5">Notes</th>
                <th className="px-4 py-2.5 text-center">Paid?</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t" style={{ borderColor: EDGE, opacity: busy === r.id ? 0.5 : 1 }} data-testid={`bumface-row-${r.id}`}>
                  <td className="px-4 py-2.5 whitespace-nowrap">{r.date}</td>
                  <td className="px-4 py-2.5">{r.hours} hrs</td>
                  <td className="px-4 py-2.5 font-semibold" style={{ color: r.paid ? GREEN : BERRY }}>{money(r.hours * (r.rate || 40))}</td>
                  <td className="px-4 py-2.5 max-w-md">{r.notes || <span className="opacity-40 italic">-</span>}</td>
                  <td className="px-4 py-2.5 text-center">
                    <button type="button" onClick={() => togglePaid(r)} data-testid={`bumface-paid-${r.id}`}
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${r.paid ? "text-white" : ""}`}
                      style={r.paid ? { backgroundColor: GREEN, borderColor: GREEN } : { borderColor: BERRY, color: BERRY }}>
                      {r.paid ? <><CheckCircle2 className="w-3.5 h-3.5" /> Paid</> : "Unpaid"}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <button type="button" onClick={() => remove(r)} title="Delete entry" data-testid={`bumface-delete-${r.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-700">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------- TASKS BOARD ---- */

function TasksBoard() {
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [staff, setStaff] = useState([]);
  const [meId, setMeId] = useState(() => localStorage.getItem("heritage-staff-me") || "");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [form, setForm] = useState({ title: "", details: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/console/hours/tasks");
      setTasks(r.data.tasks || []);
      setSummary(r.data.summary || {});
    } catch {
      toast.error("Couldn't load the task board.");
    } finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    api.get("/console/staff").then((r) => setStaff(r.data.staff || [])).catch(() => {});
    // Opening the task board marks notes as seen, clears the sidebar badge.
    localStorage.setItem("heritage-bumface-seen", new Date().toISOString());
    window.dispatchEvent(new Event("bumface-seen"));
  }, []);

  const pickMe = (id) => { setMeId(id); localStorage.setItem("heritage-staff-me", id); };

  const add = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Give the job a title."); return; }
    setBusy("add");
    try {
      await api.post("/console/hours/tasks", form);
      setForm({ title: "", details: "" });
      toast.success("Job added to the board.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save.");
    } finally { setBusy(""); }
  };

  const toggleDone = async (t) => {
    setBusy(t.id);
    try {
      await api.patch(`/console/hours/tasks/${t.id}`, { completed: !t.completed });
      await load();
    } catch { toast.error("Couldn't update."); } finally { setBusy(""); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    setBusy(t.id);
    try {
      await api.delete(`/console/hours/tasks/${t.id}`);
      toast.success("Job deleted.");
      await load();
    } catch { toast.error("Couldn't delete."); } finally { setBusy(""); }
  };

  const open = tasks.filter((t) => !t.completed);
  const done = tasks.filter((t) => t.completed);

  return (
    <div className="space-y-5" data-testid="bumface-tasks-board">
      <div className="grid grid-cols-2 gap-3 max-w-md">
        <Tile label="Open jobs" value={summary.open ?? 0} Icon={Circle} accent={BERRY} testid="bumface-tile-open" />
        <Tile label="Completed" value={summary.completed ?? 0} Icon={CheckCircle2} accent={GREEN} testid="bumface-tile-done" />
      </div>

      <div className="bg-white border rounded-md p-3 flex flex-wrap items-center gap-3" style={{ borderColor: EDGE }} data-testid="bumface-whoami">
        <span className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Chatting as</span>
        {staff.length === 0 ? (
          <span className="text-xs italic opacity-60">
            No staff yet , add the team in the <a href="/admin/staff" className="underline font-semibold not-italic" style={{ color: BERRY }}>Heritage Staff</a> tab so notes show who wrote them.
          </span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {staff.map((s) => (
              <button key={s.id} type="button" onClick={() => pickMe(s.id)} data-testid={`whoami-${s.id}`}
                className="inline-flex items-center gap-1.5 pl-1 pr-3 py-1 rounded-full border text-xs font-semibold transition-colors"
                style={meId === s.id ? { backgroundColor: BERRY, borderColor: BERRY, color: "#fff" } : { borderColor: EDGE, color: AUBERGINE }}>
                <StaffAvatar person={s} size={22} /> {s.name.split(" ")[0]}
              </button>
            ))}
          </div>
        )}
      </div>

      <form onSubmit={add} className="bg-white border rounded-md p-4 grid grid-cols-1 sm:grid-cols-[240px_1fr_auto] gap-3 items-end" style={{ borderColor: EDGE }}>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Job / task</label>
          <input placeholder="Audit April invoices" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full h-10 px-3 border rounded" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid="task-title" />
        </div>
        <div>
          <label className="text-[10px] tracking-[0.25em] uppercase font-semibold opacity-70">Details / instructions</label>
          <input placeholder="Check every invoice against the bank statement, flag anything odd…" value={form.details} onChange={(e) => setForm({ ...form, details: e.target.value })} className="mt-1 w-full h-10 px-3 border rounded" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid="task-details" />
        </div>
        <button type="submit" disabled={busy === "add"} className="h-10 px-5 rounded text-white text-sm inline-flex items-center gap-2 disabled:opacity-50" style={{ backgroundColor: BERRY }} data-testid="task-add">
          {busy === "add" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add job
        </button>
      </form>

      {loading ? (
        <div className="p-8 text-center bg-white border rounded-md" style={{ borderColor: EDGE }}><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : tasks.length === 0 ? (
        <div className="p-8 text-center italic opacity-60 bg-white border rounded-md" style={{ borderColor: EDGE }} data-testid="tasks-empty">
          No jobs yet, add the first one above ↑
        </div>
      ) : (
        <div className="space-y-3">
          {[...open, ...done].map((t) => (
            <div key={t.id} className="bg-white border rounded-md p-4" style={{ borderColor: EDGE, opacity: busy === t.id ? 0.5 : t.completed ? 0.75 : 1 }} data-testid={`task-card-${t.id}`}>
              <div className="flex items-start gap-3">
                <button type="button" onClick={() => toggleDone(t)} title={t.completed ? "Mark as open" : "Mark completed"} data-testid={`task-toggle-${t.id}`} className="mt-0.5 flex-shrink-0">
                  {t.completed
                    ? <CheckCircle2 className="w-6 h-6" style={{ color: GREEN }} />
                    : <Circle className="w-6 h-6 opacity-40 hover:opacity-80 transition-opacity" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold ${t.completed ? "line-through opacity-60" : ""}`}>{t.title}</div>
                  {t.details && <div className="text-sm mt-0.5 opacity-75">{t.details}</div>}
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-50 mt-1">
                    Added {String(t.created_at || "").slice(0, 10)}
                    {t.completed && t.completed_at ? ` · Done ${String(t.completed_at).slice(0, 10)}` : ""}
                  </div>
                  <TaskChat task={t} staff={staff} meId={meId} onPosted={load} />
                </div>
                <button type="button" onClick={() => remove(t)} title="Delete job" data-testid={`task-delete-${t.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-700 flex-shrink-0">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------- TASK CHAT ---- */

function TaskChat({ task, staff, meId, onPosted }) {
  const [text, setText] = useState("");
  const [image, setImage] = useState(null);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  const byId = Object.fromEntries(staff.map((s) => [s.id, s]));
  const messages = [
    ...(task.note ? [{ id: "legacy", text: task.note, author_name: "Note", legacy: true, at: task.created_at }] : []),
    ...(task.messages || []),
  ];

  const attach = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { setImage(await resizePhoto(file, 900)); }
    catch { toast.error("Couldn't read that photo."); }
  };

  const send = async () => {
    if (!text.trim() && !image) return;
    setSending(true);
    try {
      await api.post(`/console/hours/tasks/${task.id}/messages`, { text, image, author_id: meId || null });
      setText(""); setImage(null);
      await onPosted();
      endRef.current?.scrollIntoView({ block: "nearest" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send."); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-3 rounded-md border" style={{ borderColor: EDGE, backgroundColor: CREAM }} data-testid={`task-chat-${task.id}`}>
      {messages.length > 0 && (
        <div className="p-3 space-y-2.5 max-h-72 overflow-y-auto">
          {messages.map((m) => {
            const mine = meId && m.author_id === meId;
            const person = byId[m.author_id];
            return (
              <div key={m.id} className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
                {!m.legacy && <StaffAvatar person={person || { name: m.author_name }} size={28} />}
                <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 shadow-sm ${mine ? "rounded-br-sm" : "rounded-bl-sm"}`}
                  style={mine ? { backgroundColor: BERRY, color: "#fff" } : { backgroundColor: "#fff", border: `1px solid ${EDGE}` }}>
                  <div className={`text-[9px] tracking-[0.15em] uppercase font-semibold ${mine ? "opacity-80" : "opacity-50"}`}>
                    {m.author_name || "Unknown"}{m.author_role ? ` · ${m.author_role}` : ""}
                    {m.at ? ` · ${new Date(m.at).toLocaleDateString("en-NZ")} ${new Date(m.at).toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" })}` : ""}
                  </div>
                  {m.image && (
                    <a href={m.image} target="_blank" rel="noreferrer">
                      <img src={m.image} alt="attachment" className="mt-1.5 rounded-lg max-h-44 w-auto" />
                    </a>
                  )}
                  {m.text && <div className="text-sm mt-1 whitespace-pre-wrap break-words">{m.text}</div>}
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
      )}
      <div className="p-2.5 border-t flex items-center gap-2" style={{ borderColor: EDGE }}>
        <button type="button" onClick={() => fileRef.current?.click()} title="Attach a photo" data-testid={`chat-attach-${task.id}`}
          className="p-2 rounded-full border flex-shrink-0" style={image ? { borderColor: GOLD, backgroundColor: "#FBF3DF" } : { borderColor: EDGE }}>
          <Camera className="w-4 h-4" style={{ color: image ? "#8A6A2F" : AUBERGINE }} />
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={attach} />
        {image && (
          <span className="relative flex-shrink-0">
            <img src={image} alt="to send" className="h-9 w-9 rounded object-cover border" style={{ borderColor: GOLD }} />
            <button type="button" onClick={() => setImage(null)} className="absolute -top-1.5 -right-1.5 bg-white rounded-full border p-0.5" style={{ borderColor: EDGE }}>
              <Trash2 className="w-2.5 h-2.5 text-red-700" />
            </button>
          </span>
        )}
        <input
          placeholder="Write a note back…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          className="flex-1 h-9 px-3 border rounded-full text-sm bg-white"
          style={{ borderColor: EDGE }}
          data-testid={`chat-input-${task.id}`}
        />
        <button type="button" onClick={send} disabled={sending || (!text.trim() && !image)} data-testid={`chat-send-${task.id}`}
          className="h-9 px-4 rounded-full text-white text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
          style={{ backgroundColor: BERRY }}>
          {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
        </button>
      </div>
    </div>
  );
}

function Tile({ label, value, Icon, accent, testid }) {
  return (
    <div className="bg-white border rounded-md p-4" style={{ borderColor: EDGE }} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase font-semibold opacity-70">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}
      </div>
      <div className="text-2xl mt-1 font-semibold" style={{ color: accent }}>{value}</div>
    </div>
  );
}
